import { Fragment } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useCms } from "@/lib/cms";
import { embedMotionProps, useEmbedPreview } from "@/lib/embedPreview";
import TechStackStrip from "@/components/sections/company-v2/TechStackStrip";
import {
  parseAboutBodyParagraphs,
  parseAboutTitle,
  VALUE_ITEMS,
} from "@/components/sections/about/aboutContent";
import "./AboutSection.css";

export default function AboutSection({ embedMode: embedModeProp = false }) {
  const { cms } = useCms();
  const embedFromUrl = useEmbedPreview();
  const embedMode = embedModeProp || embedFromUrl;

  const aboutTitleParts = parseAboutTitle(cms.about_title);
  const aboutParagraphs = parseAboutBodyParagraphs(cms.about_body);

  return (
    <section id="about" data-testid="about-section" className="ds-about-section">
      <div className="ds-about-inner max-w-7xl mx-auto">
        <motion.div
          {...embedMotionProps(embedMode, {
            initial: { opacity: 0, y: 24 },
            whileInView: { opacity: 1, y: 0 },
            viewport: { once: true },
            transition: { duration: 0.6 },
          })}
          className="ds-about-stack"
        >
          <header className="ds-about-header">
            <div className="ds-about-eyebrow-row">
              <span className="ds-about-eyebrow-label">About</span>
            </div>

            <h2
              data-testid="about-title"
              className="ds-about-title ds-cms-text font-display font-medium tracking-tight whitespace-pre-line"
            >
              {aboutTitleParts.line1Before}
              {aboutTitleParts.line1Highlight ? (
                <span data-testid="about-title-highlight" className="ds-about-title-mark">
                  {aboutTitleParts.line1Highlight}
                </span>
              ) : null}
              {aboutTitleParts.line1After}
              {aboutTitleParts.line2 ? (
                <>
                  {"\n"}
                  {aboutTitleParts.line2}
                </>
              ) : null}
            </h2>

            <div className="ds-about-body-block" data-testid="about-body">
              {aboutParagraphs.map((paragraph, index) => (
                <p
                  key={index}
                  className={`ds-about-body ds-cms-text ds-about-copy-line${index > 0 ? " ds-about-body--follow" : ""}`}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </header>

          <div className="ds-about-values-panel">
            <div className="ds-about-values-grid">
              {VALUE_ITEMS.map((item, index) => (
                <Fragment key={item.label}>
                  {index > 0 ? (
                    <div className="ds-about-values-divider" aria-hidden="true" />
                  ) : null}
                  <motion.article
                    {...embedMotionProps(embedMode, {
                      initial: { opacity: 0, y: 16 },
                      whileInView: { opacity: 1, y: 0 },
                      viewport: { once: true },
                      transition: { duration: 0.55, delay: item.delay },
                    })}
                    className="ds-about-value"
                  >
                    <div className="ds-about-value-head">
                      <span className="ds-about-value-index" aria-hidden="true">
                        {item.index}
                      </span>
                      <span className="ds-about-value-label">{item.label}</span>
                    </div>
                    <p className="ds-about-value-text ds-about-copy-line">{item.text}</p>
                  </motion.article>
                </Fragment>
              ))}
            </div>
          </div>

          <div className="ds-about-actions">
            <motion.div
              {...embedMotionProps(embedMode, {
                initial: { opacity: 0, y: 8 },
                whileInView: { opacity: 1, y: 0 },
                viewport: { once: true },
                transition: { duration: 0.4, delay: 0.28 },
              })}
              data-testid="about-cta-company"
            >
              <Link to="/about" className="ds-about-more-link">
                회사소개
                <svg
                  className="ds-about-more-link__arrow"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M3.5 8h8.5M9 5.5 12 8 9 10.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <div className="ds-about-tech-panel">
        <div className="ds-about-tech-panel__inner max-w-7xl mx-auto">
          <TechStackStrip variant="dark" />
        </div>
      </div>
    </section>
  );
}
